import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, FileSpreadsheet, Printer,
  Settings2, BookOpen, Users, LogOut, Calendar, Bell, KeyRound, Link2, Activity, UserCog, Pill, Wallet,
} from "lucide-react";

export default function AdminShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pendingCounts, setPendingCounts] = useState({ household_change_requests: 0, med_change_requests: 0 });

  const loadPending = async () => {
    try {
      const { data } = await api.get("/admin/pending-summary");
      setPendingCounts(data || {});
    } catch (_e) { /* non-fatal */ }
  };

  useEffect(() => {
    loadPending();
    const t = setInterval(loadPending, 45000); // light polling so badges stay fresh
    return () => clearInterval(t);
  }, []);

  const pendingHH = pendingCounts.household_change_requests || 0;
  const pendingMeds = pendingCounts.med_change_requests || 0;

  const nav = [
    { to: "/admin", label: "Oversight", icon: LayoutDashboard, end: true },
    { to: "/admin/notifications", label: "Notifications", icon: Bell },
    { to: "/admin/change-requests", label: "Change Requests", icon: UserCog, badge: pendingHH },
    { to: "/admin/med-requests", label: "Medication Requests", icon: Pill, badge: pendingMeds },
    { to: "/admin/notes", label: "Daily Notes", icon: FileSpreadsheet },
    { to: "/admin/appointments", label: "Medical/Dental Appointments", icon: Calendar },
    { to: "/admin/budget", label: "Client Budget Tracking", icon: Wallet },
    { to: "/admin/audit", label: "Audit Report", icon: Printer },
    { to: "/admin/analytics", label: "Outcome Analytics", icon: Activity },
    { to: "/admin/forms", label: "Form Builder", icon: Settings2 },
    { to: "/admin/resources", label: "Resource Hub", icon: BookOpen },
    { to: "/admin/profiles", label: "Contractor/Client Profiles", icon: Users },
    { to: "/admin/accounts", label: "Manage Admin Accounts", icon: KeyRound },
    { to: "/admin/links", label: "Global Link Manager", icon: Link2 },
  ];

  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-secondary/30 flex flex-col">
      <header className="bg-brand-navy text-white sticky top-0 z-30 shadow-sm">
        <div className="px-6 py-3 flex items-center justify-between">
          <div className="flex items-center">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] opacity-80">OPPO CONTRACTOR PORTAL</p>
              <p className="font-display font-semibold text-sm leading-tight" data-testid="admin-name">{user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {pendingHH > 0 && (
              <button
                onClick={() => navigate("/admin/change-requests")}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-400 text-brand-navy hover:bg-amber-300 text-xs font-semibold transition-colors"
                data-testid="pending-banner"
              >
                <Bell className="w-3.5 h-3.5" />
                {pendingHH} pending household change{pendingHH === 1 ? "" : "s"}
              </button>
            )}
            {pendingMeds > 0 && (
              <button
                onClick={() => navigate("/admin/med-requests")}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-rose-400 text-brand-navy hover:bg-rose-300 text-xs font-semibold transition-colors"
                data-testid="pending-med-banner"
              >
                <Pill className="w-3.5 h-3.5" />
                {pendingMeds} pending med request{pendingMeds === 1 ? "" : "s"}
              </button>
            )}
            <Button
              variant="ghost" size="sm"
              onClick={handleLogout}
              className="text-white hover:bg-white/10 hover:text-white gap-1.5"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" /> <span className="text-xs font-semibold">Log out</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        <aside className="w-64 bg-white border-r border-border flex flex-col sticky top-[60px] h-[calc(100vh-60px)]">
          <nav className="flex-1 p-3 space-y-1 overflow-auto">
            {nav.map(({ to, label, icon: Icon, end, badge }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                    isActive
                      ? "bg-brand-navy text-white font-semibold"
                      : "text-foreground hover:bg-secondary"
                  }`
                }
                data-testid={`admin-nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span className="leading-tight flex-1">{label}</span>
                {badge > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold" data-testid={`nav-badge-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    {badge}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main className="flex-1 p-8 overflow-x-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
